"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PageableStore = void 0;
const tslib_1 = require("tslib");
const core_common_1 = require("@smallstack/core-common");
const typesystem_1 = require("@smallstack/typesystem");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const object_store_1 = require("./object-store");
const ojaat_1 = require("./ojaat");
const store_1 = require("./store");
class PageableStore extends object_store_1.ObjectStore {
    constructor(options = {}) {
        super(options === null || options === void 0 ? void 0 : options.initialValue);
        this.options = options;
        // paging state
        this.pagination$ = new rxjs_1.BehaviorSubject({ totalElements: 0, totalPages: 1 });
        this.query$ = new rxjs_1.BehaviorSubject({ page: 1, size: 10 });
        this.currentPageIds$ = new rxjs_1.BehaviorSubject([]);
        this.currentSearch$ = new rxjs_1.BehaviorSubject([]);
        this.currentPage$ = (0, rxjs_1.combineLatest)([this.currentPageIds$, this.value$]).pipe((0, operators_1.map)(([pageIds]) => pageIds.map((pageId) => this.getById(pageId))));
        this.pageableQueue = new ojaat_1.OneJobAtATimeQueue();
        // init with defaults
        if (!options.timeout)
            options.timeout = 30000;
        this.query$.next({ page: 1, size: 10 });
    }
    reset() {
        const _super = Object.create(null, {
            reset: { get: () => super.reset }
        });
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const currentQuery = this.query$.value;
            this.query$.next({ page: 1, size: 10, search: currentQuery === null || currentQuery === void 0 ? void 0 : currentQuery.search, sort: currentQuery === null || currentQuery === void 0 ? void 0 : currentQuery.sort });
            this.currentSearch$.next([]);
            yield _super.reset.call(this);
        });
    }
    reload() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.reset();
            return this.load();
        });
    }
    reloadOne(id) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const model = yield this.get(id, true);
            this.addValue(model);
            return model;
        });
    }
    load(options = { silently: false }) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            // lock another loading if store is already loading
            if (this.state === store_1.StoreState.LOADING)
                yield this.waitForLoaded();
            if ((options === null || options === void 0 ? void 0 : options.silently) !== true) {
                this.setState(store_1.StoreState.LOADING);
                this.setLoading();
            }
            try {
                if (!this.options.timeout)
                    this.options.timeout = 30000;
                const page = yield (0, rxjs_1.from)(this.loadModels(this.query$.value))
                    .pipe((0, operators_1.timeout)(this.options.timeout), (0, operators_1.first)())
                    .toPromise();
                if (page && page.elements instanceof Array) {
                    this.addValues(page.elements);
                    this.pagination$.next({
                        totalElements: page.totalElements,
                        totalPages: page.totalPages
                    });
                    this.currentPageIds$.next(page.elements.map((elem) => elem.id));
                }
                this.setState(store_1.StoreState.LOADED);
            }
            catch (e) {
                this.setError(e);
                if (this.state === store_1.StoreState.LOADING)
                    this.setState(store_1.StoreState.INITIAL);
            }
        });
    }
    /**
     * Either returns a copy of the model from store or, if not found, from http
     */
    get(id, forceHttp = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (id === undefined) {
                throw new Error("id cannot be undefined");
            }
            try {
                let modelById = this.getById(id); // get local value
                if (modelById && !forceHttp)
                    return modelById;
                modelById = yield this.pageableQueue.run(id, () => (0, rxjs_1.from)(this.loadModels({
                    search: typesystem_1.SQBuilder.asString([{ fieldname: "_id", value: id, matcher: typesystem_1.SearchByFieldMatcher.EQUALS }])
                }))
                    .toPromise()
                    .then((page) => page.elements[0]));
                if (modelById)
                    this.addValue(modelById);
                return modelById;
            }
            catch (e) {
                // not found
            }
        });
    }
    /**
     * This is a very expensive method and should only be used if there is absolutely no other way
     */
    preload(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            let page = 1;
            let totalPages;
            do {
                const current = this.query$.value;
                current.page = page;
                current.size = 1000;
                this.query$.next(current);
                yield this.load(options);
                page++;
                totalPages = this.pagination$.value.totalPages;
            } while (page <= totalPages);
            // set defaults again
            this.query$.next(Object.assign(Object.assign({}, this.query$.value), { page: 1, size: 10 }));
        });
    }
    setPageSize(pageSize) {
        core_common_1.Logger.debug("PageableStore", "Setting page size: " + pageSize);
        const current = this.query$.value;
        current.size = pageSize;
        this.query$.next(current);
    }
    setPage(pageNumber) {
        core_common_1.Logger.debug("PageableStore", "Setting page: " + pageNumber);
        const current = this.query$.value;
        current.page = pageNumber;
        this.query$.next(current);
    }
    sortBy(property) {
        const current = this.query$.value;
        current.sort = property;
        current.page = 1;
        this.query$.next(current);
    }
    /** Clones the current store, so mutations won't affect the original store */
    clone() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const outer = this;
        class InnerPageableStore extends PageableStore {
            constructor() {
                super();
            }
            loadModels(query) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return outer.loadModels(query);
            }
        }
        return new InnerPageableStore();
    }
    createSearch(searchQuery, searchOptions = {}, autoload = true) {
        var _a;
        if (!searchOptions.page)
            searchOptions.page = 1;
        if (!searchOptions.size)
            searchOptions.size = ((_a = this.query$.value) === null || _a === void 0 ? void 0 : _a.size) || 10;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const outer = this;
        class InnerPageableStore extends PageableStore {
            constructor() {
                super();
                this.query$.next(Object.assign({ search: typesystem_1.SQBuilder.toBase64String(searchQuery) }, searchOptions));
                if (autoload)
                    void this.load();
            }
            loadModels(query) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return outer.loadModels(query);
            }
        }
        return new InnerPageableStore();
    }
    searchByFields(searchByFields, logicalOperator = "or") {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            let currentQuery = this.query$.value;
            if (currentQuery === undefined)
                currentQuery = {};
            currentQuery.page = 1;
            currentQuery.search = typesystem_1.SQBuilder.asString(searchByFields, logicalOperator);
            this.currentSearch$.next(searchByFields);
            this.query$.next(currentQuery);
            yield this.load();
        });
    }
    search(searchQuery) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const query = this.query$.value;
            query.page = 1;
            query.search = typesystem_1.SQBuilder.toBase64String(searchQuery);
            this.query$.next(query);
            yield this.load();
        });
    }
    /** Performs a query via http, does not store the result in the store. Can be used for dynamic searches. */
    query(query) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const searchStore = this.createSearch(query);
            yield searchStore.load();
            return searchStore.value;
        });
    }
    /**
     *
     * @param willBeOneMore if you already know that this request will return one more than you currently have (e.g. cause you just created one), set this to true, otherwise the newly created item might land on a new last page and we're showing the second last here
     */
    gotoLastPage(willBeOneMore = false) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const currentQuery = this.query$.value;
            const currentPagination = this.pagination$.value;
            currentQuery.page = currentPagination.totalPages;
            if (willBeOneMore && currentPagination.totalElements / currentPagination.totalPages === currentQuery.size)
                currentQuery.page++;
            this.query$.next(currentQuery);
            yield this.load();
        });
    }
    // for crud interface
    getMany(query) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.loadModels(query);
        });
    }
}
exports.PageableStore = PageableStore;
